/*!
 * Copyright 2017 by ChartIQ, Inc.
 * All rights reserved.
 */
import { BaseService } from "../baseService";
import { ResponderMessage, RouterListenerUnsubscribe, RouterResponse } from "../../clients/routerClient";
import { Globals } from "../../common/Globals";
import store, { AppDispatch, initializeModules } from "./store";
import {
	SERVER_CHANNEL,
	ContextAny,
	InteropMessage,
	InteropUnregister,
	InteropCurrentContext,
	InteropCurrentContextRequest,
	InteropFindIntentRequest,
	InteropFindIntentResponse,
	InteropFindIntentsByContext,
	InteropIntentsList,
	InteropSystemChannels,
	InteropRaiseIntentResponse,
	InteropOpenResponse,
	InteropGetCurrentChannelsResponse,
	InteropGetCurrentChannels,
} from "../../clients/Interop/types";
import { uuidsFromWindowName } from "./modules/commonUtils";
import resolver, {
	findAndFilterAppIntents,
	filterIntentsByContext,
	filterAppHandlersFromIntents,
} from "./modules/resolver";
import { actionIdentifier, actionResult } from "../../common/redux/actionIdMapper";
import { LAUNCHER_SERVICE } from "../../common/constants";
import {
	ResolverIntent,
	ResolverChannelMeta,
	ResolverStore,
	RaiseIntentActionResult,
	OpenActionResult,
	CLEANUP_INTERVAL,
} from "./types";
import { AppIntent, ResolveError } from "../../typedefs/FDC3";
import Logger from "../../clients/logger";
import ConfigClient from "../../clients/configClient";
import { AnyAction } from "@reduxjs/toolkit";
import { isEqual } from "lodash";
import testPoint from "../../common/testPoint";
import linker from "./modules/linker";

Logger.start();

Logger.system.log("interopService: Starting");

const {
	register,
	unregister,
	broadcast,
	subscribeContext,
	unsubscribeContext,
	joinChannels,
	leaveChannels,
	open,
	raiseIntent,
	subscribeIntent,
	unsubscribeIntent,
	addAppDefinitions,
	removeAppDefinitions,
	registerUIResolver,
	setInteropRegistrationData,
	cleanupAndVerify,
} = resolver.actions;

// Make a strongly typed dispatch function
const { dispatch }: { dispatch: AppDispatch } = store;
class InteropService extends BaseService {
	/**
	 * The interop service subscribes to a channel to learn about [graceful] window closings.
	 */
	#unsubscribeWindowClosings: RouterListenerUnsubscribe | undefined;

	/**
	 * The interop service subscribes to a heartbeat channel. Connected apps send heartbeats which
	 * allows the service to detect ungraceful closings.
	 */
	#unsubscribeHeartbeats: RouterListenerUnsubscribe | undefined;

	/**
	 * The interop service periodically checks to ensure that all open apps have registered for the intents
	 * that they claim to accept.
	 */
	#cleanAndVerifyInterval: number = 0;

	constructor(params: any) {
		super(params);
		this.initialize = this.initialize.bind(this);
		this.onBaseServiceReady(this.initialize);
		this.start();
	}

	listenForWindowClosings() {
		this.#unsubscribeWindowClosings = this.RouterClient.addListener(LAUNCHER_SERVICE.WINDOW_CLOSED, (err, response) => {
			if (err) {
				console.log(`Error in listenForWindowClosings() ${err}`);
			} else {
				const { name: windowName } = response?.data;
				const state = store.getState();
				uuidsFromWindowName({ clients: state.resolver.clients, windowName }).forEach((uuid) => {
					const fsaUnregister: InteropUnregister = {
						type: "unregister",
						meta: {
							uuid,
						},
					};
					dispatch(resolver.actions.unregister(fsaUnregister));
				});
			}
		});
	}

	startCleanAndVerifyLoop() {
		if (Globals.setInterval) {
			this.#cleanAndVerifyInterval = Globals.setInterval(() => {
				if (store.getState().root.heartbeats) store.dispatch(cleanupAndVerify());
			}, CLEANUP_INTERVAL);
		}
	}

	stopCleanAndVerifyLoop() {
		if (this.#cleanAndVerifyInterval) clearInterval(this.#cleanAndVerifyInterval);
	}

	currentContextRequest(fsa: InteropCurrentContextRequest): InteropCurrentContext {
		const { contextType } = fsa.payload;
		const { channel } = fsa.meta;
		const { lastChannelState, lastChannelStateByType } = store.getState()?.resolver;

		// FDC3 spec says to return null if no context state
		let currentContext: ContextAny | null = null;

		// When a context type is provided, the most recent context matching the type will be returned to the client, or an undefined context if no matching context is found.
		// If no context type is provided, the most recent context that was broadcast on the channel - regardless of type - will be returned.
		// If no context has been set on the channel, then an undefined context is returned.
		if (channel && contextType) {
			currentContext = lastChannelStateByType?.[channel][contextType].context;
		} else if (channel && lastChannelState[channel]) {
			currentContext = lastChannelState[channel].context;
		} else if (!channel) {
			Logger.system.error("interopService:currentContextRequest must specify channel", fsa);
		}

		// response that will be sent to fdc3 client
		let response: InteropCurrentContext = {
			type: "currentContext",
			payload: {
				context: currentContext,
			},
			meta: {
				uuid: "currentContextRequestUUID",
			},
		};
		Logger.system.log("interopService:currentContextRequest", {
			fsa,
			response,
		});

		return response;
	}

	// handles fdc3.findIntent request
	findIntent = (fsa: InteropFindIntentRequest): InteropFindIntentResponse => {
		const { intent: intentName, context } = fsa.payload;
		const resolverState: ResolverStore = store.getState()?.resolver;

		let appIntents: AppIntent[] = findAndFilterAppIntents({
			resolverState,
			intentName,
			context,
			filterNotLaunchable: true,
			originUuid: fsa.meta.uuid,
		});
		let appIntent: AppIntent | undefined = appIntents.length > 0 ? appIntents[0] : undefined;

		// response that will be sent to fdc3 client
		let response: InteropFindIntentResponse = {
			type: "findIntentResponse",
			payload: {
				appIntent,
				error: appIntent ? null : ResolveError.NoAppsFound,
			},
			meta: {
				uuid: "aFindIntentResponseUUID",
			},
		};
		Logger.system.log("interopService:findIntent", {
			response,
			intentName,
			appIntent,
		});

		return response;
	};

	// handles fdc3.findIntentsByContext request
	findIntentsByContext(fsa: InteropFindIntentsByContext): InteropIntentsList {
		const { context } = fsa.payload;
		const resolverState = store.getState()?.resolver;
		const intents = resolverState?.intents;

		// filter intents by the specified context
		const filteredIntents = filterIntentsByContext(intents, context);

		// strip from intent handlers the apps that don't support the specified context, including non-launchable that aren't opened
		const strippedIntents = filterAppHandlersFromIntents({
			resolverState,
			intents: filteredIntents,
			context,
			filterNotLaunchable: true,
		});

		// after filtering and stripping the intent data by context, pull out the intent properties to form an array of Intents to be returned in response
		const intentsList: AppIntent[] = strippedIntents.map((element: ResolverIntent) => {
			return {
				intent: element.info,
				apps: Object.keys(element.launchableHandlers).map((appName) => {
					return { name: appName };
				}),
			};
		});

		// response that will be sent to fdc3 client
		const response: InteropIntentsList = {
			type: "intentsList",
			payload: {
				intents: intentsList,
				error: intentsList.length > 0 ? null : ResolveError.NoAppsFound,
			},
			meta: {
				uuid: store.getState().resolver.config.serverUUID,
			},
		};

		Logger.system.log("interopService:findIntentsByContext", {
			response,
			context,
			intents,
			filteredIntents,
			strippedIntents,
			intentsList,
		});

		return response;
	}

	// handles fdc3.getSystemChannels request
	getSystemChannels(): InteropSystemChannels {
		const systemChannelMeta: ResolverChannelMeta = store.getState()?.resolver?.channelMeta;

		let channels = Object.keys(systemChannelMeta);

		// response that will be sent to fdc3 client
		let response: InteropSystemChannels = {
			type: "systemChannels",
			payload: {
				channels,
			},
			meta: {
				uuid: "systemChannelsUUID",
			},
		};
		Logger.system.log("interopService:getSystemChannels", { response, systemChannelMeta, channels });

		return response;
	}

	getCurrentChannels(fsa: InteropGetCurrentChannels): InteropGetCurrentChannelsResponse {
		const client = store.getState()?.resolver?.clients[fsa.meta.uuid];

		const channels: { channel: string }[] = (client?.joinedChannels ?? []).map((channel) => {
			return {
				channel,
			};
		});

		const payload = client
			? {
					data: channels,
			  }
			: {
					error: "Client UUID not found",
			  };

		const response: InteropGetCurrentChannelsResponse = {
			type: "interopGetCurrentChannelsResponse",
			payload,
			meta: {
				uuid: "getCurrentChannelsUUID",
			},
		};

		Logger.system.log("interopService:getCurrentChannels", { response });

		return response;
	}

	async initialize(cb: () => void) {
		initializeModules();
		this.listenForWindowClosings();
		this.startCleanAndVerifyLoop();

		const { err, data: maxTimeout } = await ConfigClient.getValue({
			field: "finsemble.servicesConfig.interop.addIntentListenerMaxInitializationTimeout",
		});
		if (!err && maxTimeout) {
			dispatch(resolver.actions.addIntentListenerMaxInitializationTimeout(maxTimeout));
		}

		this.Logger.system.log(`interopService: Desktop Agent starting up on ${SERVER_CHANNEL}`);
		await this.RouterClient.addResponder(SERVER_CHANNEL, async (error, queryMessage) => {
			if (error) {
				console.log("Error adding responder", error);
			} else {
				if (queryMessage) this.handleIncoming(queryMessage);
			}
		});
		cb();
	}

	/**
	 * Stops and cleans up the service
	 */
	async stop() {
		this.RouterClient.removeResponder(SERVER_CHANNEL);
		this.stopCleanAndVerifyLoop();
		if (this.#unsubscribeHeartbeats) this.#unsubscribeHeartbeats();
		if (this.#unsubscribeWindowClosings) this.#unsubscribeWindowClosings();
	}

	/**
	 * Checks to make sure that the client is valid before dispatching the action.
	 * The client is valid if there is a client entry in state for the given uuid
	 * and if the origin windowName matches the client's windowName in state
	 */
	dispatchWithValidation(action: AnyAction, header: RouterResponse<any>["header"]) {
		const fsa = action.payload.fsa || action.payload;
		const { uuid } = fsa.meta;
		let client = store.getState().resolver.clients[uuid];
		if (!client) {
			const debug = `interopService: dispatchWithValidation(): assertion failed: no client exists for uuid=${uuid}. Probably an app that doesn't use FDC3.`;
			Logger.system.debug(debug, action);
			return;
		}

		// confirm message is from the same transport the client registered on
		if (!isEqual(header.originIncomingTransportInfo, client.authenticatedTransport)) {
			const error = `interopService: dispatchWithValidation(): assertion failed: client message didn't match authenticated transport of origin for uuid=${uuid}`;
			console.assert(client, error, action);
			Logger.system.error(error, action);
			return;
		}
		dispatch(action);
	}

	async handleIncoming(message: ResponderMessage<any>) {
		const { data, header } = message;
		Logger.system.debug("interopService: handleIncoming Message", data, header);
		let fsa: InteropMessage = data;
		// Most messages have no reply, so we'll automatically respond with no data
		// For messages that require a response, such as findIntentsByContext, set this to false
		let autoRespond = true;

		switch (fsa.type) {
			case "register":
				var registerActionId = actionIdentifier(fsa.type);
				// append the transport of origin to be saved in client state; used in dispatchWithValidation to verify messages are coming from the authenticated client
				fsa.meta.authenticatedTransport = header.originIncomingTransportInfo;
				fsa.meta.routerOrigin = header.origin;
				dispatch(register({ fsa, actionId: registerActionId }));
				// wait until resolver has completed registration before responding back to client
				await actionResult(registerActionId);
				message.sendQueryResponse(null);
				autoRespond = false;
				break;
			case "unregister":
				this.dispatchWithValidation(unregister(fsa), header);
				break;
			case "systemChannelRequest":
				message.sendQueryResponse(null, this.getSystemChannels());
				autoRespond = false;
				break;
			case "broadcast":
				this.dispatchWithValidation(broadcast(fsa), header);
				break;
			case "subscribeContext":
				this.dispatchWithValidation(subscribeContext(fsa), header);
				break;
			case "unsubscribeContext":
				this.dispatchWithValidation(unsubscribeContext(fsa), header);
				break;
			case "joinChannels":
				this.dispatchWithValidation(joinChannels(fsa), header);
				break;
			case "leaveChannels":
				this.dispatchWithValidation(leaveChannels(fsa), header);
				break;
			case "getCurrentChannels":
				message.sendQueryResponse(null, this.getCurrentChannels(fsa));
				autoRespond = false;
				break;
			case "currentContextRequest":
				message.sendQueryResponse(null, this.currentContextRequest(fsa));
				autoRespond = false;
				break;
			case "findIntent":
				message.sendQueryResponse(null, this.findIntent(fsa));
				autoRespond = false;
				break;
			case "findIntentsByContext":
				message.sendQueryResponse(null, this.findIntentsByContext(fsa));
				autoRespond = false;
				break;
			case "open":
				var openActionId = actionIdentifier(fsa.type);
				this.dispatchWithValidation(open({ fsa, actionId: openActionId }), header);
				var openActionResponse = <OpenActionResult>await actionResult(openActionId);
				var openResponse: InteropOpenResponse = {
					type: "openResponse",
					payload: { error: openActionResponse.error },
				};
				message.sendQueryResponse(null, openResponse);
				Logger.system.log("interopService: actionResponse for open", openActionResponse, openResponse);
				autoRespond = false;
				break;
			case "raiseIntent":
				var raiseIntentActionId = actionIdentifier(fsa.type);
				this.dispatchWithValidation(raiseIntent({ fsa, actionId: raiseIntentActionId }), header);
				var raiseIntentActionResponse = <RaiseIntentActionResult>await actionResult(raiseIntentActionId);
				var resolution: InteropRaiseIntentResponse = {
					type: "intentResolution",
					payload: {
						error: raiseIntentActionResponse.error,
						data: raiseIntentActionResponse.result,
					},
				};
				message.sendQueryResponse(null, resolution);
				Logger.system.log("interopService: actionResponse for raiseIntent", raiseIntentActionResponse, resolution);
				autoRespond = false;
				break;
			case "setInteropRegistrationData":
				// This is a simple check that prevents bad actors from creating their own authentication tokens.
				if (message.header.origin.includes("RouterClient.windowService")) {
					dispatch(setInteropRegistrationData(fsa));
				} else {
					Logger.system.error("setInteropRegistrationData dropped because not from the window service", message);
				}
				break;
			case "subscribeIntent":
				this.dispatchWithValidation(subscribeIntent(fsa), header);
				break;
			case "unsubscribeIntent":
				this.dispatchWithValidation(unsubscribeIntent(fsa), header);
				break;
			case "addAppDefinitions":
				dispatch(addAppDefinitions(fsa));
				break;
			case "removeAppDefinitions":
				dispatch(removeAppDefinitions(fsa));
				break;
			case "registerUIResolver":
				dispatch(registerUIResolver(fsa));
				break;
			default:
				break;
		}
		if (autoRespond) message.sendQueryResponse(null);
	}
}

// Used by Live Mocha tests to control state or simulate user actions. See fdc3Tests.js for example where Testpoint.triggerAction causes this onAction() to fire.
testPoint.onAction("Linker.linkChannel", (params) => {
	store.dispatch(
		linker.actions.linkChannels({
			type: "linker/linkChannels",
			payload: {
				channels: [params.channelName],
			},
			meta: {
				windowName: params.windowName,
			},
		})
	);
});
testPoint.onAction("Linker.unlinkChannel", (params) => {
	store.dispatch(
		linker.actions.unlinkChannels({
			type: "linker/unlinkChannels",
			payload: {
				channels: [params.channelName],
			},
			meta: {
				windowName: params.windowName,
			},
		})
	);
});

export default new InteropService({
	startupDependencies: {},
	shutdownDependencies: {},
});
